import type {
  CorrectShareTypeVersionMutationResult,
  CreateShareTypeMutationResult,
  UpdateShareTypeMutationResult,
} from 'generated/gql';
import {
  type Register,
  type ShareType,
  type ShareTypeInput,
  useCorrectShareTypeVersionMutation,
  useCreateShareTypeMutation,
  useUpdateShareTypeMutation,
} from 'generated/gql';
import { t } from '@lingui/macro';
import type { SidebarFormProps } from 'components/SidebarForm/SidebarForm';
import { SidebarFormContainer, SidebarFormContent } from 'components/SidebarForm/SidebarForm';
import { setFormErrors } from 'forms/helpers/setFormErrors';
import type {
  ShareTypeFormProps,
  ShareTypeEditFormSchema,
} from 'forms/sharetype/ShareTypeEditForm';
import {
  getShareTypeEditFormSchema,
  mapShareTypeEditFormValuesToShareTypeInput,
  mapShareTypeVersionToShareTypeEditFormValues,
  ShareTypeEditForm,
  ShareTypeFormMode,
} from 'forms/sharetype/ShareTypeEditForm';

import type { ShareTypeVersionWithShareType } from 'helpers/shareTypes';
import { useGlobalError } from 'hooks/useGlobalError';
import { useMemo } from 'react';

interface GetShareTypeFromResponseProps {
  shareTypeVersion?: ShareTypeVersionWithShareType;
  shareTypeInput: ShareTypeInput;
  registerId: string;
}
const useShareTypeSidebarFormMutation = (mode: ShareTypeFormMode) => {
  const [createShareType] = useCreateShareTypeMutation();
  const [correctShareTypeVersion] = useCorrectShareTypeVersionMutation();
  const [updateShareType] = useUpdateShareTypeMutation();
  switch (mode) {
    case ShareTypeFormMode.Create:
      return {
        updateMutation: createShareType,
        getShareTypeFromResponse: (response: NonNullable<CreateShareTypeMutationResult['data']>) =>
          response.createShareType,
        getVariables: ({ shareTypeInput, registerId }: GetShareTypeFromResponseProps) => ({
          shareType: shareTypeInput,
          registerId,
        }),
      };
    case ShareTypeFormMode.Correct:
      return {
        updateMutation: correctShareTypeVersion,
        getShareTypeFromResponse: (
          response: NonNullable<CorrectShareTypeVersionMutationResult['data']>,
        ) => response.correctShareTypeVersion,
        getVariables: ({ shareTypeInput, shareTypeVersion }: GetShareTypeFromResponseProps) => ({
          shareType: shareTypeInput,
          shareTypeVersionId: shareTypeVersion?.id,
        }),
      };
    case ShareTypeFormMode.Update:
      return {
        updateMutation: updateShareType,
        getShareTypeFromResponse: (response: NonNullable<UpdateShareTypeMutationResult['data']>) =>
          response.updateShareType,
        getVariables: ({ shareTypeInput, shareTypeVersion }: GetShareTypeFromResponseProps) => ({
          shareType: shareTypeInput,
          shareTypeId: shareTypeVersion?.shareType.id,
        }),
      };
    default:
      throw new Error(`Invalid mode: ${mode}`);
  }
};

interface GetShareTypeVersionDefaultValuesProps {
  mutationId?: string;
  isFoundation: boolean;
}
const getShareTypeVersionDefaultValues = ({
  mutationId,
  isFoundation,
}: GetShareTypeVersionDefaultValuesProps) => {
  const defaultValues = {
    ...getShareTypeEditFormSchema(isFoundation).getDefault(),
    createdInMutationId: mutationId,
  };
  if (isFoundation) defaultValues.name = t`Certificaten`;
  return defaultValues;
};

interface ShareTypeSidebarFormProps {
  register: Register;
  shareTypeVersion?: ShareTypeVersionWithShareType;
  isSidebarShown: boolean;
  onSidebarClose: () => any;
  onUpdate: (shareType: ShareType) => any;
  mutationId?: string;
  mutations?: ShareTypeFormProps['mutations'];
  mode: ShareTypeFormMode;
}
export const ShareTypeSidebarForm = ({ isSidebarShown, ...props }: ShareTypeSidebarFormProps) => {
  return (
    <SidebarFormContainer isDrawerShown={isSidebarShown} width={720}>
      {isSidebarShown && <ShareTypeSidebarFormContent {...props} />}
    </SidebarFormContainer>
  );
};

const ShareTypeSidebarFormContent = ({
  register,
  shareTypeVersion,
  onSidebarClose,
  onUpdate,
  mutationId,
  mutations,
  mode,
}: Omit<ShareTypeSidebarFormProps, 'isSidebarShown'>) => {
  const [, setGlobalError] = useGlobalError();
  const { isFoundation } = register.legalEntity;
  const { updateMutation, getShareTypeFromResponse, getVariables } =
    useShareTypeSidebarFormMutation(mode);

  const defaultValues = useMemo(
    () =>
      shareTypeVersion
        ? mapShareTypeVersionToShareTypeEditFormValues(shareTypeVersion, mutationId)
        : getShareTypeVersionDefaultValues({ mutationId, isFoundation }),
    [isFoundation, mutationId, shareTypeVersion],
  );

  const header = useMemo(() => {
    if (isFoundation) {
      return mode === ShareTypeFormMode.Create
        ? t`Create certificate type`
        : t`Edit certificate type`;
    }
    return mode === ShareTypeFormMode.Create
      ? t`Aandelensoort aanmaken`
      : t`Aandelensoort wijzigen`;
  }, [isFoundation]);

  const formSchema = useMemo(() => getShareTypeEditFormSchema(isFoundation), [isFoundation]);

  const form: SidebarFormProps<typeof ShareTypeEditFormSchema> = {
    defaultValues,
    header,
    subtitle: isFoundation
      ? t`Fill in the properties of the certificate type.`
      : t`Vul de eigenschappen in van de soort aandelen.`,
    form: (
      <ShareTypeEditForm
        canEditCreatedInMutation={mode === ShareTypeFormMode.Correct && !mutationId}
        currentUnderlyingShareType={shareTypeVersion?.underlyingShareType}
        isFoundation={isFoundation}
        mutations={mutations}
        stakId={register.id}
      />
    ),
    formSchema,
    onSubmit: async (formValues, form) => {
      const shareTypeInput = mapShareTypeEditFormValuesToShareTypeInput(formValues);
      const variables: any = getVariables({
        shareTypeInput,
        shareTypeVersion,
        registerId: register.id,
      });
      await updateMutation({
        variables,
        onCompleted: (response: any) => {
          const shareType = getShareTypeFromResponse(response);
          onUpdate(shareType as ShareType);
          onSidebarClose();
        },
        onError: ({ graphQLErrors }) => {
          setFormErrors({ errors: graphQLErrors, setGlobalError, form });
        },
      });
    },
    setDrawerClosed: onSidebarClose,
  };

  return <SidebarFormContent {...form} />;
};
